import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { Button, Typography, Tabs, Tab, Paper, TextField, MenuItem } from '@material-ui/core';
import PubSub from 'pubsub-js';
import Auth from '../services/auth';
import AdminSettingsDataAccess from '../services/adminSettingsDataAccess';
import './AdminSettings.css';

const auth = new Auth();
const api = new AdminSettingsDataAccess();
const changeId = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 3) | 8)).toString(16);
});
export function validation(item, value) {
    if (item.Kind === 'boolean' && value !== '1' && value !== '0') return 'Select enabled or disabled.';
    if (item.Kind === 'integer' && (!/^[+-]?\d+$/.test(value) || Number(value) > 2147483647 || Number(value) < -2147483648)) return 'Enter a whole number between -2147483648 and 2147483647.';
    if (item.Kind === 'json') {
        try {
            const parsed = JSON.parse(value);
            if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return 'Enter one JSON object.';
        } catch (e) { return 'Enter one valid JSON object.'; }
    }
    return '';
}

export default class AdminSettings extends Component {
    state = { tab: 0, account: auth.getActiveAccount(), items: [], policies: [], drafts: {}, error: '', notice: '', fetched: '',
        loading: false, saving: null, historyScope: 'features', history: [], page: 1, hasMore: false, filter: '' };
    generation = 0;
    mounted = false;
    pendingRead = null;
    pendingSave = false;
    componentDidMount() {
        if (auth.getHomeAccount() !== '998') return;
        this.mounted = true;
        this.token = PubSub.subscribe('AccountChanged', this.accountChanged);
        document.addEventListener('visibilitychange', this.focusRefresh);
        window.addEventListener('focus', this.focusRefresh);
        this.timer = setInterval(this.focusRefresh, 5000);
        this.refresh();
    }
    componentWillUnmount() {
        this.mounted = false;
        this.generation++;
        clearInterval(this.timer);
        if (this.token) PubSub.unsubscribe(this.token);
        document.removeEventListener('visibilitychange', this.focusRefresh);
        window.removeEventListener('focus', this.focusRefresh);
    }
    focusRefresh = () => {
        if (!document.hidden && this.state.tab !== 2) this.refresh();
    };
    accountChanged = () => {
        this.generation++;
        this.pendingRead = null;
        this.setState({ account: auth.getActiveAccount(), items: [], policies: [], drafts: {}, history: [], page: 1, fetched: '', error: '',
            notice: 'Account changed. Unsaved edits were cleared.', saving: null, loading: false }, this.refresh);
    };
    scope = () => this.state.tab === 0 || (this.state.tab === 2 && this.state.historyScope === 'features') ? null : this.state.account;
    changeTab = (event, tab) => {
        if (this.pendingSave) return;
        if (Object.keys(this.state.drafts).length && !window.confirm('Discard unsaved edits and change view?')) return;
        this.generation++;
        this.pendingRead = null;
        this.setState({ tab, items: [], policies: [], drafts: {}, history: [], page: 1, fetched: '', error: '', notice: '', loading: false }, this.refresh);
    };
    refresh = async () => {
        if (!this.mounted || this.pendingRead !== null || this.pendingSave || auth.getHomeAccount() !== '998') return;
        const generation = this.generation;
        const account = this.scope();
        if (account !== null && !account) return;
        this.pendingRead = generation;
        this.setState({ loading: true });
        try {
            const data = this.state.tab === 2 ? await api.history(account, this.state.page, this.state.filter) : await api.get(account);
            if (!this.mounted || generation !== this.generation) return;
            if (this.state.tab === 2) this.setState({ history: data.Items || [], hasMore: data.HasMore, fetched: new Date().toLocaleTimeString(), error: '' });
            else this.setState(previous => {
                const drafts = { ...previous.drafts };
                (data.Items || []).forEach(item => {
                    if (drafts[item.Key] && drafts[item.Key].version !== item.Version) drafts[item.Key] = { ...drafts[item.Key], conflict: true };
                });
                return { items: data.Items || [], policies: data.Policies || [], drafts, environment: data.Environment, fetched: new Date().toLocaleTimeString(), error: '' };
            });
        } catch (e) {
            if (this.mounted && generation === this.generation) this.setState({ error: 'Unable to refresh. Previously displayed values may be stale.' });
        } finally {
            if (this.pendingRead === generation) this.pendingRead = null;
            if (this.mounted && generation === this.generation) this.setState({ loading: false });
        }
    };
    edit = (item, value) => {
        this.setState(previous => ({ notice: '', drafts: { ...previous.drafts, [item.Key]: {
            value, version: previous.drafts[item.Key] ? previous.drafts[item.Key].version : item.Version,
            id: changeId(), conflict: previous.drafts[item.Key] ? previous.drafts[item.Key].conflict : false
        } } }));
    };
    discard = key => this.setState(previous => {
        const drafts = { ...previous.drafts }; delete drafts[key]; return { drafts, notice: '' };
    }, this.refresh);
    save = async item => {
        const draft = this.state.drafts[item.Key];
        if (!draft || draft.conflict || validation(item, draft.value) || this.pendingSave || this.pendingRead !== null) return;
        const generation = this.generation;
        const account = this.scope();
        this.pendingSave = true;
        this.setState({ saving: item.Key, error: '', notice: '' });
        try {
            if (account === null && draft.value === '1') {
                let impact;
                try { impact = await api.impact(item.Key); }
                catch (e) {
                    if (this.mounted && generation === this.generation)
                        this.setState({ error: 'Unable to load the affected-account preview. No new save was submitted. Retry the preview before enabling this feature.' });
                    return;
                }
                if (!this.mounted || generation !== this.generation) return;
                if (!impact || impact.FeatureKey !== item.Key || !Number.isInteger(impact.AffectedAccountCount)
                    || impact.AffectedAccountCount < 0 || !Number.isInteger(impact.InvalidAccountCount) || impact.InvalidAccountCount < 0) {
                    this.setState({ error: 'The affected-account preview is invalid. No new save was submitted.' });
                    return;
                }
                const confirmed = window.confirm(`Enable ${item.Label || item.Key}?\n\nAffected accounts: ${impact.AffectedAccountCount}\n${impact.CountDescription || ''}\nEvaluated at: ${impact.EvaluatedAt || 'Not supplied'}\n\nCounts are sampled and may change. Accounts requiring configuration reconciliation: ${impact.InvalidAccountCount}.\n\nSelect OK to submit this setting change.`);
                if (!confirmed || !this.mounted || generation !== this.generation) return;
            }
            const result = await api.save(account, item.Key, { Value: draft.value, ExpectedVersion: draft.version || null, ChangeId: draft.id });
            if (!this.mounted || generation !== this.generation) return;
            this.setState(previous => {
                const drafts = { ...previous.drafts }; delete drafts[item.Key];
                return { drafts, notice: 'Setting saved.', items: previous.items.map(row => row.Key === item.Key ? { ...row, Value: result.Value, Version: result.Version, Exists: true } : row) };
            });
        } catch (e) {
            if (!this.mounted || generation !== this.generation) return;
            const status = e.response && e.response.status;
            if (status === 409) this.setState(previous => ({ error: 'Setting changed. Discard this edit, reload and review before saving again.', drafts: {
                ...previous.drafts, [item.Key]: { ...draft, conflict: true }
            } }));
            else this.setState({ error: status === 400 ? 'The server rejected this value. Review the setting requirements.' :
                'Save was not confirmed. Retry the unchanged edit to check the same change safely, or refresh to review the saved value.' });
        } finally {
            this.pendingSave = false;
            if (this.mounted) this.setState({ saving: null }, () => { if (generation !== this.generation) this.refresh(); });
        }
    };
    historyChange = changes => {
        this.generation++;
        this.pendingRead = null;
        this.setState({ ...changes, history: [], fetched: '', loading: false }, this.refresh);
    };
    renderEditor = item => {
        const draft = this.state.drafts[item.Key];
        let value = draft ? draft.value : (item.Value === null || item.Value === undefined ? '' : item.Value);
        if (item.Kind === 'boolean') value = value === 'true' ? '1' : value === 'false' ? '0' : value;
        const error = validation(item, value);
        return <Paper key={item.Key} elevation={0} className={`admin-setting-card${item.Kind === 'json' ? ' admin-setting-wide' : ''}`}>
            <Typography variant="h6" className="admin-setting-title">{item.Label || item.Key}</Typography>
            <Typography color="textSecondary" className="admin-setting-description">{item.Description}</Typography>
            {item.Error && <Typography color="error">This setting is unavailable for editing. See its description for details.</Typography>}
            <TextField id={`admin-setting-${this.state.tab}-${encodeURIComponent(item.Key)}`} select={item.Kind === 'boolean'} label={item.Label || item.Key} value={value}
                multiline={item.Kind === 'json'} fullWidth margin="normal" variant="outlined" size="small" disabled={!item.Editable || !!this.state.saving}
                error={!!draft && !!error} helperText={draft && error} onChange={e => this.edit(item, e.target.value)}>
                {item.Kind === 'boolean' ? [<MenuItem key="unset" value="" disabled>Not configured</MenuItem>,
                    <MenuItem key="true" value="1">Enabled</MenuItem>, <MenuItem key="false" value="0">Disabled</MenuItem>] : null}
            </TextField>
            {draft && draft.conflict && <Typography color="error">A newer value is available. Your edit is preserved. Discard it to review the current value.</Typography>}
            <div className="admin-setting-actions"><Button color="primary" variant="contained" disableElevation disabled={!item.Editable || !draft || !!error || draft.conflict || !!this.state.saving || this.state.loading}
                onClick={() => this.save(item)}>{this.state.saving === item.Key ? 'Saving...' : 'Save'}</Button>
            {draft && <Button disabled={!!this.state.saving} onClick={() => this.discard(item.Key)}>Discard edit and reload</Button>}</div>
        </Paper>;
    };
    render() {
        if (auth.getHomeAccount() !== '998') return <Redirect to="/" />;
        const s = this.state;
        return <section aria-label="Administration" className="admin-settings-page">
            <header className="admin-settings-header"><div><Typography variant="h4">Administration</Typography><Typography color="textSecondary">Manage feature availability and account configuration.</Typography></div></header>
            <Typography className="admin-settings-context" color="textSecondary">Selected account: {s.account || 'None'} {s.environment ? ` | Environment: ${s.environment}` : ''}</Typography>
            <Tabs className="admin-settings-tabs" indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" value={s.tab} onChange={this.changeTab}><Tab label="Features" /><Tab label="Account settings" /><Tab label="History" /></Tabs>
            {s.error && <Typography role="alert" color="error">{s.error}</Typography>}
            {s.notice && <Typography role="status">{s.notice}</Typography>}
            <div className="admin-settings-toolbar"><Button variant="outlined" color="primary" size="small" onClick={this.refresh} disabled={s.loading || !!s.saving}>Refresh</Button>
            <Typography variant="caption">{s.fetched ? `Last refreshed: ${s.fetched}` : 'Not yet refreshed'}</Typography></div>
            {s.tab !== 2 ? <div>
                <Typography className="admin-settings-note" variant="body2" color="textSecondary">Settings refresh every five seconds while this page is visible. Unsaved edits are preserved.</Typography>
                {s.tab === 1 && s.policies.length > 0 && <section aria-label="Configured workflow policy" className="admin-policy-section">
                    <Typography variant="h6">Configured workflow policy</Typography>
                    <Typography variant="body2" color="textSecondary">Current availability based on saved global and account settings.</Typography><div className="admin-policy-grid">
                    {s.policies.map(policy => <Paper key={policy.Workflow} elevation={0} className="admin-policy-card">
                        <Typography variant="subtitle1" className="admin-policy-title">{policy.Workflow}</Typography>
                        <Typography>Upload: {policy.UploadMode === 'Classic' ? 'Classic available; bulk disabled' : policy.UploadEnabled ? 'Enabled' : 'Unavailable'} | Mode: {policy.UploadMode}</Typography>
                        <Typography>OCR: {policy.OcrEnabled ? 'Enabled' : 'Disabled'}{policy.Workflow === 'Transcription' ? ' (built into Transcription Upload)' : ''}</Typography>
                        {(policy.UploadReasons || []).map((reason, index) => <Typography key={`upload-${index}`}>Upload: {reason}</Typography>)}
                        {(policy.OcrReasons || []).map((reason, index) => <Typography key={`ocr-${index}`}>OCR: {reason}</Typography>)}
                    </Paper>)}</div>
                </section>}
                <div className="admin-setting-grid">{s.items.map(this.renderEditor)}</div>
            </div> : <div>
                <div className="admin-history-filters"><TextField variant="outlined" size="small" id="admin-history-scope" select label="History scope" value={s.historyScope} onChange={e => this.historyChange({ historyScope: e.target.value, page: 1 })}>
                    <MenuItem value="features">Global features</MenuItem><MenuItem value="account">Selected account</MenuItem>
                </TextField>
                <TextField variant="outlined" size="small" id="admin-history-setting-filter" label="Setting key filter" value={s.filter} onChange={e => this.setState({ filter: e.target.value })} />
                <Button variant="outlined" color="primary" onClick={() => this.historyChange({ page: 1 })}>Apply filter</Button></div>
                <Typography className="admin-settings-note" variant="body2" color="textSecondary">History updates when you select Refresh.</Typography>
                {s.history.map(row => <Paper key={row.ChangeId} elevation={0} className="admin-history-entry">
                    <Typography variant="subtitle1" className="admin-policy-title">{row.SettingKey} - {row.Action}</Typography>
                    <Typography variant="caption" color="textSecondary">{row.Actor} | {row.ChangedAt} | Source: {row.WriteSource || 'Not recorded'}</Typography>
                    <Typography style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>Before: {row.OldValue === null ? '(not configured)' : row.OldValue}</Typography>
                    <Typography style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>After: {row.NewValue === null ? '(not configured)' : row.NewValue}</Typography>
                </Paper>)}
                <div className="admin-history-pagination"><Button disabled={s.page === 1 || s.loading} onClick={() => this.historyChange({ page: s.page - 1 })}>Previous</Button>
                <Typography component="span">Page {s.page}</Typography>
                <Button disabled={!s.hasMore || s.loading} onClick={() => this.historyChange({ page: s.page + 1 })}>Next</Button></div>
            </div>}
        </section>;
    }
}



// WEBPACK FOOTER //
// ./src/components/AdminSettings.js